import { Fragment } from "react"
import {
	Alert,
	Badge,
	Box,
	Button,
	Heading,
	Icons,
	Inline,
	Spinner,
	Stack,
	Text,
	Time,
} from "@sembark-travel/ui-base"
import { Markdown } from "@sembark-travel/ui-markdown"
import { useXHR } from "@sembark-travel/xhr"
import useSWR from "swr"
import { useNavigate } from "../router-utils"
import {
	paymentPoliciesXHR,
	TPaymentPolicy,
	TPaymentPolicyDetail,
} from "./store"
import {
	RuleBlock,
	NewPaymentPolicyForm,
	TNewPaymentPolicyFormSchema,
} from "./NewItem"
import {
	diffPaymentRuleSets,
	formatPaymentRuleItemParts,
	formValuesToPayload,
	policyVersionToFormValues,
	TPaymentRuleSetDiffEntry,
} from "./utils"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { parseDate } from "@sembark-travel/datetime-utils"
import type { TCustomerPaymentPreference } from "../CustomerPaymentPreferences"
import {
	DiffPill,
	VersionHistoryDialog as GenericVersionHistoryDialog,
} from "../shared/Policy"
import {
	diffTone,
	formatDateRangeRuleLabel,
	type TDiffSide,
	type TDiffStatus,
} from "../shared/policyDiffUtils"

export function PaymentPolicyItemDetails({
	detailId,
	detailName,
	isDisabled = false,
	isDefault = false,
	versionId,
	version,
}: {
	detailId: number
	detailName: string
	isDisabled?: boolean
	isDefault?: boolean
	versionId?: number
	version?: number
}) {
	const navigate = useNavigate()
	const hasMultipleVersions = Number(version) > 1

	return (
		<Stack>
			{/* Header */}
			<Inline
				paddingX="2"
				paddingY="4"
				gap="2"
				alignItems="center"
				borderBottomWidth="1"
			>
				<Box
					rounded="md"
					paddingX="2"
					paddingY="1"
					cursor="pointer"
					bgColor={{ hover: "gray2" }}
					onClick={() => navigate(-1)}
					title="Go back"
				>
					<Icons.ArrowLeft size="5" />
				</Box>
				<Inline gap="3" alignItems="center" flexWrap="wrap">
					<Heading as="h4">{detailName}</Heading>
					{version && versionId ? (
						<VersionHistoryDialog
							detailId={detailId}
							currentVersionId={versionId}
							currentVersion={version}
						>
							{({ open }) => (
								<Box
									paddingX="1"
									rounded="md"
									backgroundColor={hasMultipleVersions ? "gray1" : "inset"}
									cursor={hasMultipleVersions ? "pointer" : "default"}
									onClick={hasMultipleVersions ? open : undefined}
									title={
										hasMultipleVersions ? "Version History" : "First version"
									}
								>
									<Text color="muted" fontWeight="semibold">
										v{version}
									</Text>
								</Box>
							)}
						</VersionHistoryDialog>
					) : null}
					{isDefault ? <Badge warning>Default</Badge> : null}
				</Inline>
			</Inline>

			{/* Default policy warning */}
			{isDefault ? (
				<Box paddingX="6" paddingTop="4">
					<Alert status="warning">
						This policy is applicable when no other payment policy is specified.
						e.g. If a Hotel does not set another payment policy explicitly, then
						this policy gets applied for that hotel.
					</Alert>
				</Box>
			) : null}

			{/* Policy content */}
			{versionId ? (
				<PolicyVersionDetail
					versionId={versionId}
					detailId={detailId}
					detailName={detailName}
					isDisabled={isDisabled}
					isDefault={isDefault}
				/>
			) : (
				<Stack
					padding="6"
					justifyContent="center"
					alignItems="center"
					style={{ minHeight: "20rem" }}
				>
					<Text color="muted" fontWeight="semibold">
						No Policy Found.
					</Text>
				</Stack>
			)}
		</Stack>
	)
}

function PolicyVersionDetail({
	versionId,
	detailId,
	detailName,
	isDisabled,
	isDefault,
}: {
	versionId: number
	detailId: number
	detailName: string
	isDisabled: boolean
	isDefault?: boolean
}) {
	const xhr = useXHR()
	const navigate = useNavigate()

	const {
		data: policy,
		error,
		isLoading,
		isValidating,
	} = useSWR<TPaymentPolicy, Error>(`/payment-policies/${versionId}`, () =>
		paymentPoliciesXHR(xhr).getVersionById(versionId)
	)

	if (isLoading) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Spinner />
			</Stack>
		)
	}

	if (error) {
		return (
			<Box padding="6">
				<Alert status="error">
					{error.message || "Failed to load policy details. Please try again."}
				</Alert>
			</Box>
		)
	}

	if (!policy) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Text color="muted" fontWeight="semibold">
					No Policy Found.
				</Text>
			</Stack>
		)
	}

	const baseRuleSet = policy.rule_sets.find(
		(r) => r.valid_from == null && r.valid_to == null
	)
	const dateRuleSets = policy.rule_sets.filter(
		(r) => r.valid_from != null || r.valid_to != null
	)
	const policyVersion = policy.version

	function goToEdit() {
		const params = new URLSearchParams({
			name: detailName,
			version_id: String(versionId),
			version: String(policyVersion),
			is_default: String(isDefault),
		})
		navigate(
			{
				pathname: "/payment-policy/:paymentPolicyId/edit",
				search: params.toString(),
			},
			{ params: { paymentPolicyId: String(detailId) }, replace: true }
		)
	}

	return (
		<Stack gap="4" paddingX="6" paddingY="4">
			{/* Metadata row + Edit button */}
			<Inline
				gap="8"
				flexWrap="wrap"
				alignItems="center"
				justifyContent="between"
			>
				<Inline gap="8" flexWrap="wrap" alignItems="center">
					<Stack gap="px">
						<Text fontSize="sm">Last updated on</Text>
						<Time color="muted" timestamp={policy.created_at} dateFormat />
					</Stack>
					<Stack gap="px">
						<Text fontSize="sm">Last updated by</Text>
						<Text color="muted">{policy.created_by?.name}</Text>
					</Stack>
					{isValidating ? <Icons.Spinner size="4" title="Loading..." /> : null}
				</Inline>
				{!isDisabled ? (
					<Button status="primary" level="primary" onClick={goToEdit}>
						Edit policy
					</Button>
				) : null}
			</Inline>

			{/* Base rule block */}
			{baseRuleSet ? (
				<RuleBlock isBase rules={baseRuleSet.rules} readOnly />
			) : null}

			{/* Date-specific rule blocks */}
			{dateRuleSets.map((ruleSet) => (
				<RuleBlock
					key={ruleSet.id}
					isBase={false}
					validFrom={ruleSet.valid_from ? parseDate(ruleSet.valid_from) : null}
					validTo={ruleSet.valid_to ? parseDate(ruleSet.valid_to) : null}
					rules={ruleSet.rules}
					readOnly
				/>
			))}

			{/* Additional information */}
			{policy.additional_info ? (
				<Stack gap="2">
					<Text fontWeight="semibold">Additional information</Text>
					<Markdown>{policy.additional_info}</Markdown>
				</Stack>
			) : null}
		</Stack>
	)
}

export function PaymentPolicyVersionView({ versionId }: { versionId: number }) {
	const xhr = useXHR()
	const {
		data: policy,
		error,
		isLoading,
	} = useSWR<TPaymentPolicy, Error>(`/payment-policies/${versionId}`, () =>
		paymentPoliciesXHR(xhr).getVersionById(versionId)
	)

	if (isLoading) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Spinner />
			</Stack>
		)
	}
	if (error) {
		return (
			<Alert status="error">
				{error.message || "Failed to load policy details. Please try again."}
			</Alert>
		)
	}
	if (!policy) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Text color="muted" fontWeight="semibold">
					No Policy Found.
				</Text>
			</Stack>
		)
	}

	const baseRuleSet = policy.rule_sets.find(
		(r) => r.valid_from == null && r.valid_to == null
	)
	const dateRuleSets = policy.rule_sets.filter(
		(r) => r.valid_from != null || r.valid_to != null
	)

	return (
		<Stack gap="4">
			<Inline gap="8" flexWrap="wrap">
				<Stack gap="px">
					<Text fontSize="sm">Created on</Text>
					<Time color="muted" timestamp={policy.created_at} dateFormat />
				</Stack>
				<Stack gap="px">
					<Text fontSize="sm">Created by</Text>
					<Text color="muted">{policy.created_by?.name}</Text>
				</Stack>
			</Inline>
			{baseRuleSet ? (
				<RuleBlock isBase rules={baseRuleSet.rules} readOnly />
			) : null}
			{dateRuleSets.map((ruleSet) => (
				<RuleBlock
					key={ruleSet.id}
					isBase={false}
					validFrom={ruleSet.valid_from ? parseDate(ruleSet.valid_from) : null}
					validTo={ruleSet.valid_to ? parseDate(ruleSet.valid_to) : null}
					rules={ruleSet.rules}
					readOnly
				/>
			))}
			{policy.additional_info ? (
				<Stack gap="2">
					<Text fontWeight="semibold">Additional information</Text>
					<Markdown>{policy.additional_info}</Markdown>
				</Stack>
			) : null}
		</Stack>
	)
}

/**
 * Loads the current policy version by `versionId`, pre-populates the form,
 * and calls `onSuccess` with the updated detail on save.
 */
export function EditPaymentPolicy({
	detailId,
	versionId,
	detailName,
	isDefault,
	onSuccess,
	onCancel,
}: {
	detailId: number
	versionId: number
	detailName: string
	isDefault: boolean
	onSuccess: (updatedDetail: TPaymentPolicyDetail) => void
	onCancel: () => void
}) {
	const xhr = useXHR()

	const {
		data: policy,
		isLoading,
		error,
	} = useSWR<TPaymentPolicy, Error>(`/payment-policies/${versionId}`, () =>
		paymentPoliciesXHR(xhr).getVersionById(versionId)
	)

	if (isLoading) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Spinner />
			</Stack>
		)
	}

	if (error) {
		return (
			<Box padding="6">
				<Alert status="error">
					{error.message || "Failed to load policy details. Please try again."}
				</Alert>
			</Box>
		)
	}

	if (!policy) {
		return (
			<Stack
				padding="6"
				justifyContent="center"
				alignItems="center"
				style={{ minHeight: "20rem" }}
			>
				<Text color="muted" fontWeight="semibold">
					No Policy Found.
				</Text>
			</Stack>
		)
	}

	const initialValues: TNewPaymentPolicyFormSchema = policyVersionToFormValues(
		detailName,
		policy
	)

	return (
		<NewPaymentPolicyForm
			isEdit
			isDefault={isDefault}
			initialValues={initialValues}
			onCancel={onCancel}
			onSubmit={async (values) => {
				const updatedDetail = await paymentPoliciesXHR(xhr).update(
					detailId,
					formValuesToPayload(values)
				)
				if ("message" in updatedDetail) {
					showSnackbar(updatedDetail.message)
					return
				}
				showSnackbar("Payment policy updated successfully")
				onSuccess(updatedDetail)
			}}
		/>
	)
}

export { DiffPill }

export function VersionHistoryDialog({
	detailId,
	currentVersionId,
	currentVersion,
	children,
}: {
	detailId: number
	currentVersionId: number
	currentVersion: number
	children: (props: { open: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()

	return (
		<GenericVersionHistoryDialog<TPaymentPolicy>
			detailId={detailId}
			currentVersionId={currentVersionId}
			currentVersion={currentVersion}
			versionsKey={(id) => `/payment-policy-heads/${id}/payment-policies`}
			fetchVersions={(id) =>
				paymentPoliciesXHR(xhr).getVersions(id, { page: 1, limit: 100 })
			}
			versionKey={(id) => `/payment-policies/${id}`}
			fetchVersion={(id) => paymentPoliciesXHR(xhr).getVersionById(id)}
			renderDiffEntries={(previous, current) => {
				const entries = diffPaymentRuleSets(
					previous.rule_sets || [],
					current.rule_sets || []
				)
				return {
					hasChanges: entries.length > 0,
					entries: entries.map((entry) => (
						<RuleSetDiffCard key={entry.key} entry={entry} />
					)),
				}
			}}
		>
			{children}
		</GenericVersionHistoryDialog>
	)
}

function RuleSetDiffCard({ entry }: { entry: TPaymentRuleSetDiffEntry }) {
	return (
		<Box borderWidth="1" rounded="md" overflow="hidden">
			<Box
				display="grid"
				overflowX="auto"
				style={{ gridTemplateColumns: "1fr 1fr" }}
			>
				<RuleSetHeaderCell
					ruleSet={entry.previous}
					status={entry.status}
					side="previous"
				/>
				<RuleSetHeaderCell
					ruleSet={entry.current}
					status={entry.status}
					side="current"
				/>
				{entry.items.map((row) => (
					<Fragment key={row.key}>
						<RuleItemCell
							item={row.previous}
							status={row.status}
							side="previous"
						/>
						<RuleItemCell
							item={row.current}
							status={row.status}
							side="current"
						/>
					</Fragment>
				))}
			</Box>
		</Box>
	)
}

function RuleSetHeaderCell({
	ruleSet,
	status,
	side,
}: {
	ruleSet:
		| TPaymentRuleSetDiffEntry["previous"]
		| TPaymentRuleSetDiffEntry["current"]
	status: TDiffStatus
	side: TDiffSide
}) {
	const tone =
		status === "added" || status === "removed" ? diffTone(status, side) : null
	return (
		<Box padding="2" bgColor="subtle">
			{ruleSet ? (
				<DiffPill tone={tone} fontWeight="semibold">
					{formatDateRangeRuleLabel(ruleSet)}
				</DiffPill>
			) : null}
		</Box>
	)
}

function RuleItemCell({
	item,
	status,
	side,
}: {
	item: TCustomerPaymentPreference | null
	status: TDiffStatus
	side: TDiffSide
}) {
	const parts = item ? formatPaymentRuleItemParts(item) : null
	const tone = diffTone(status, side)
	return (
		<Box
			paddingX="2"
			paddingY="3"
			borderTopWidth="1"
			style={{ minWidth: "20rem" }}
		>
			{parts ? (
				<Box
					paddingX="2"
					paddingY="1"
					rounded="md"
					bgColor={
						tone === "removed"
							? "danger"
							: tone === "added"
								? "success"
								: undefined
					}
				>
					<Text
						fontSize="sm"
						fontWeight="semibold"
						color={
							tone === "removed"
								? "danger"
								: tone === "added"
									? "success"
									: undefined
						}
						textDecoration={tone === "removed" ? "lineThrough" : undefined}
					>
						{parts.condition}
					</Text>
					{parts.body ? (
						<Text
							fontSize="sm"
							fontWeight="normal"
							color={
								tone === "removed"
									? "danger"
									: tone === "added"
										? "success"
										: undefined
							}
							textDecoration={tone === "removed" ? "lineThrough" : undefined}
							style={{ whiteSpace: "pre-line" }}
						>
							{parts.body}
						</Text>
					) : null}
				</Box>
			) : null}
		</Box>
	)
}
